<template>
    <UploadEntityDocumentModal
        :rp-name="rpName"
        :submitting="isUploading"
        :error="error"
        @submit="onSubmit"
        @close="$emit('abort')"
    />
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { buildBinaryDataObjectFromFile } from '@evidentid/file-utils/prepareFile';

    import UploadEntityDocumentModal
        from '@/modules/dashboard/components/UploadEntityDocumentModal/UploadEntityDocumentModal.vue';
    import { ENTITY_DETAILS_TABS } from '@/modules/entity-details/constants/entityDetailsTabs.constants';
    import { useEntityEventsStore } from '@/modules/entity-events/store';
    import { useEntityTagsStore } from '@/modules/entity-tags/store';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s
    // (both directly and forwarded from ProcedureTarget's `abort`/`finish` contract) --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    export default defineComponent({
        name: 'UploadEntityDocumentProcedure',
        components: { UploadEntityDocumentModal },
        props: {
            // PRODUCT-26110 QA fixes4: ProcedureTarget.vue passes `:uuid="execution.uuid"`
            // to every procedure component it dynamically renders (see ProcedureTarget's
            // <component :is=... :uuid=...> and ProcedureManager's execution identity).
            // No procedure consumes the value; it's declared here purely so Vue 3 doesn't
            // warn "Extraneous non-props attributes (uuid)" on fragment-root procedures.
            uuid: {
                type: String,
                default: '',
            },
            rpName: {
                type: String as PropType<string>,
                required: true,
            },
            entityId: {
                type: String as PropType<string>,
                required: true,
            },
        },
        emits: [ 'abort', 'finish' ],
        data() {
            return {
                isUploading: false,
                error: '',
            };
        },
        methods: {
            async onSubmit({ file, tags }: { file: File, tags: string[] }): Promise<void> {
                this.isUploading = true;
                this.error = '';
                try {
                    const gatewayClient = (this as any).$gatewayClient;
                    const binaryDataObject = await buildBinaryDataObjectFromFile(file);
                    await gatewayClient.submissionTprmUi().uploadEntityDocument(
                        this.rpName, this.entityId, {
                            filename: file.name,
                            document: binaryDataObject.data,
                            tags,
                        },
                    );
                    useSnackbarStore().displaySnackbar({
                        // TODO: Add view url to the link per designs, as part of
                        //  https://evidentid.atlassian.net/browse/PRODUCT-25517
                        message: 'File uploaded successfully',
                        permanent: false,
                        success: true,
                        timeout: 3000,
                    });
                    if ((this as any).$route?.query?.tab === ENTITY_DETAILS_TABS.EVENTS) {
                        useEntityEventsStore().loadActivityHistory({
                            rpName: this.rpName,
                            entityId: this.entityId,
                            filters: {},
                        });
                        useEntityTagsStore().loadTags({ rpName: this.rpName });
                    }
                    this.$emit('finish');
                } catch (e) {
                    this.error = 'Something went wrong. Please try again.';
                } finally {
                    this.isUploading = false;
                }
            },
        },
    });
</script>
